import { css, cva } from '@repo/panda/css';
import type { SpacingToken, SystemStyleObject } from '@repo/panda/types';
import type React from 'react';

const flexColumnStyles = cva({
    base: {
        display: 'flex',
        flexDirection: 'column',
    },
    variants: {
        justify: {
            start: { justifyContent: 'flex-start' },
            end: { justifyContent: 'flex-end' },
            center: { justifyContent: 'center' },
            between: { justifyContent: 'space-between' },
            around: { justifyContent: 'space-around' },
            evenly: { justifyContent: 'space-evenly' },
        },
        align: {
            start: { alignItems: 'flex-start' },
            end: { alignItems: 'flex-end' },
            center: { alignItems: 'center' },
            baseline: { alignItems: 'baseline' },
            stretch: { alignItems: 'stretch' },
        },
        alignSelf: {
            start: { alignSelf: 'flex-start' },
            end: { alignSelf: 'flex-end' },
            center: { alignSelf: 'center' },
            baseline: { alignSelf: 'baseline' },
            stretch: { alignSelf: 'stretch' },
        },
        wrap: {
            nowrap: { flexWrap: 'nowrap' },
            wrap: { flexWrap: 'wrap' },
            'wrap-reverse': { flexWrap: 'wrap-reverse' },
        },
        width: {
            auto: { width: 'auto' },
            full: { width: 'full' },
        },
        height: {
            auto: { height: 'auto' },
            full: { height: 'full' },
        },
    },
    defaultVariants: {
        justify: 'start',
        align: 'start',
        wrap: 'nowrap',
        width: 'auto',
        height: 'auto',
    },
});

export type FlexColumnVariants = NonNullable<Parameters<typeof flexColumnStyles>[0]>;

export interface FlexColumnProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
    justify?: FlexColumnVariants['justify'];
    align?: FlexColumnVariants['align'];
    alignSelf?: FlexColumnVariants['alignSelf'];
    gap?: SpacingToken;
    wrap?: FlexColumnVariants['wrap'];
    width?: FlexColumnVariants['width'];
    height?: FlexColumnVariants['height'];
    children?: React.ReactNode;
    className?: SystemStyleObject; // Pass `css.raw({})` from components, not a class string.
}

export const FlexColumn: React.FC<FlexColumnProps> = ({
    justify,
    align,
    alignSelf,
    gap = 'none',
    wrap,
    width,
    height,
    children,
    className,
    ...props
}: FlexColumnProps) => {
    const flexColumnClassName = flexColumnStyles.raw({ justify, align, alignSelf, wrap, width, height });

    return (
        <div className={css(flexColumnClassName, className, css.raw({ gap }))} {...props}>
            {children}
        </div>
    );
};
